import { ArrowLeftRight } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import PageHeader from '../../components/PageHeader';

const GATEWAY_LABELS = { razorpay: 'Razorpay', stripe: 'Stripe', bank_transfer: 'Bank Transfer', manual: 'Manual' };

const STATUS_STYLES = {
  success: 'bg-[#EBF7E6] text-[#2E844A]',
  pending: 'bg-[#FEF1E5] text-[#DD7A01]',
  failed: 'bg-[#FEF1EE] text-[#BA0517]',
  cancelled: 'bg-[#F7F9FB] text-[#706E6B]',
};

export default function SuperAdminPaymentTransactions() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [gatewayFilter, setGatewayFilter] = useState('');

  const load = () => {
    setLoading(true);
    api.get('/admin/payments', { params: { limit: 100, status: statusFilter || undefined, gateway: gatewayFilter || undefined } })
      .then(res => setPayments(res.data.payments || []))
      .catch(() => setError('Failed to load payment transactions.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [statusFilter, gatewayFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalSuccess = payments.filter(p => p.status === 'success').reduce((sum, p) => sum + (p.amount || 0), 0);

  return (
    <div className="w-full pb-10 space-y-6">
      <PageHeader title="SaaS Payment Transactions" icon={ArrowLeftRight} subtitle="Actual gateway transaction attempts against the platform's own subscription billing — distinct from the Invoice ledger and from CRM invoice payment logs." />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white border border-[#E5E5E5] rounded-lg p-4 shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
          <p className="text-xs text-[#706E6B]">Transactions shown</p>
          <p className="text-2xl font-bold text-[#080707]">{payments.length}</p>
        </div>
        <div className="bg-white border border-[#E5E5E5] rounded-lg p-4 shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
          <p className="text-xs text-[#706E6B]">Successful total (shown page)</p>
          <p className="text-2xl font-bold text-[#080707]">{totalSuccess.toLocaleString()}</p>
        </div>
        <div className="bg-white border border-[#E5E5E5] rounded-lg p-4 flex items-center gap-2 shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="h-9 px-2 border border-[#D8DDE6] rounded text-[13px] flex-1">
            <option value="">All statuses</option>
            <option value="success">Success</option>
            <option value="pending">Pending</option>
            <option value="failed">Failed</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <select value={gatewayFilter} onChange={e => setGatewayFilter(e.target.value)} className="h-9 px-2 border border-[#D8DDE6] rounded text-[13px] flex-1">
            <option value="">All gateways</option>
            <option value="razorpay">Razorpay</option>
            <option value="stripe">Stripe</option>
            <option value="bank_transfer">Bank Transfer</option>
            <option value="manual">Manual</option>
          </select>
        </div>
      </div>

      <div className="bg-white border border-[#E5E5E5] rounded-lg p-4 shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-[#2E2E2E]">Transactions</h3>
          <button onClick={load} disabled={loading} className="h-9 px-3 border border-[#E5E5E5] rounded whitespace-nowrap text-[13px] font-semibold text-[#5C5C5C] hover:bg-[#F7F9FB] disabled:opacity-50">
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
        {error ? (
          <p className="text-sm text-[#BA0517] py-4">{error}</p>
        ) : loading && payments.length === 0 ? (
          <p className="text-sm text-[#706E6B] py-8 text-center">Loading…</p>
        ) : payments.length === 0 ? (
          <p className="text-sm text-[#706E6B] py-8 text-center">No transactions recorded yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#F3F2F2]">
                  <th className="text-left py-2 pr-4 font-semibold text-[#706E6B]">Organization</th>
                  <th className="text-left py-2 pr-4 font-semibold text-[#706E6B]">Gateway</th>
                  <th className="text-left py-2 pr-4 font-semibold text-[#706E6B]">Amount</th>
                  <th className="text-left py-2 pr-4 font-semibold text-[#706E6B]">Status</th>
                  <th className="text-left py-2 pr-4 font-semibold text-[#706E6B]">Transaction ID</th>
                  <th className="text-left py-2 pr-4 font-semibold text-[#706E6B]">When</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F3F2F2]">
                {payments.map(p => (
                  <tr key={p._id}>
                    <td className="py-2.5 pr-4">
                      <div className="font-medium text-[#2E2E2E]">{p.companyId?.companyName || 'Unknown'}</div>
                      <div className="text-xs text-[#706E6B]">{p.companyId?.email || ''}</div>
                    </td>
                    <td className="py-2.5 pr-4 text-[#444444]">{GATEWAY_LABELS[p.gateway] || p.gateway}</td>
                    <td className="py-2.5 pr-4 font-medium text-[#2E2E2E]">{p.currency} {p.amount}</td>
                    <td className="py-2.5 pr-4">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_STYLES[p.status] || STATUS_STYLES.pending}`}>{p.status.toUpperCase()}</span>
                    </td>
                    <td className="py-2.5 pr-4 text-xs text-[#706E6B] font-mono">{p.transactionId || '—'}</td>
                    <td className="py-2.5 pr-4 text-xs text-[#706E6B]">{new Date(p.createdAt).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
